import { mediaUrl } from './api';
import { hash } from './hash';

/**
 * How a person is drawn: their photograph if they have taken one, and their
 * initials if they have not.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THERE IS NO FALLBACK FACE. There used to be: sixteen real selfies from a CC0
 * Kaggle set, checked into `assets/faces/`, assigned by handle hash, and drawn
 * for anybody with no photograph of their own. They are gone (docs/5 § D260805-0422).
 *
 * They were scaffolding that outlived its reason. Their own README said they
 * left when in-app capture landed, and capture landed in D61 — but the deeper
 * problem is what they were doing on the surfaces that still had them: an
 * account with no photograph is a PERMANENT and ordinary state here, because the
 * capture step can be declined, so the stock face was not standing in for a
 * picture that was on its way. It was a stranger's face under somebody else's
 * name, indefinitely, in a product whose whole proposition is that the person
 * you are writing to is who you think they are.
 *
 * `EditProfileScreen` had already worked this out and opted out by hand — its
 * comment is the argument, and it only ever applied to that one screen because
 * that is the one screen with a viewer who can tell. D260805-0422 is that exception
 * becoming the rule.
 * ─────────────────────────────────────────────────────────────────────────────
 */

/**
 * The image to draw for a person, or nothing — in which case the surface draws
 * the monogram below.
 *
 * ONE RESOLVER, AND NOW ONE SURFACE TOO. A face used to be drawn two ways — as a
 * round avatar (`Face`) and as postage on a letter (`LetterStamp`) — each reaching
 * for the fixtures directly, so adding real photographs to one left the other
 * drawing a stranger's stock selfie beside the same name on the same screen. Since
 * docs/5 § D260805-1523 there is exactly one renderer of a person's photograph, and
 * nothing left to fall back TO, which makes this one line.
 *
 * IT STAYS A FUNCTION ANYWAY. "Where a person's picture comes from" is exactly the
 * sort of thing that grows a second case — a cached copy, a moderation hold, a
 * signed URL — and the bug above is what two call sites discovering that separately
 * looks like. The next surface that needs a face calls this one.
 */
export function faceUrlFor(author: {
  handle: string;
  avatarMediaId?: string;
}): string | undefined {
  return author.avatarMediaId ? mediaUrl(author.avatarMediaId) : undefined;
}

/**
 * One letter, or two where there is a second word. Never three: this is worn at
 * 26px inside a stamp, where a third initial stops being readable before it
 * starts being useful.
 */
export function initials(displayName: string): string {
  const parts = displayName.trim().split(/\s+/).filter(Boolean);
  const first = parts[0]?.[0] ?? '?';
  const last = parts.length > 1 ? parts[parts.length - 1]?.[0] : undefined;
  return (first + (last ?? '')).toUpperCase();
}

/**
 * The monogram's hue, from the handle, as an inline style to be paired with the
 * `monogram` class.
 *
 * THE CLASS OWNS EVERYTHING THAT DECIDES LEGIBILITY and this owns only the hue —
 * see `index.css`, which pins lightness and chroma per scheme and records the
 * measured contrast. Twelve hues, 30° apart: enough that two people in the same
 * conversation are unlikely to collide, few enough that the set reads as one
 * family rather than as a colour picker.
 *
 * DERIVED FROM THE HANDLE RATHER THAN THE NAME, so the colour holds when
 * somebody edits their display name, and is identical on every surface without
 * anything being stored. It changes when a handle changes (D65 releases the old
 * one), which is the right way round: the monogram follows the address.
 */
export function monogramStyle(handle: string): React.CSSProperties {
  const hue = 25 + (hash(handle) % 12) * 30;
  /* Cast because custom properties are not in `CSSProperties`. React has passed
     them through to the style attribute since 16.6; the type has not caught up. */
  return { '--monogram-hue': hue } as React.CSSProperties;
}
